import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { formatDuration, parseDuration, type HoursFormat } from '@tt/shared';
import { Input } from '@/components/ui/input';

interface DurationInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  /** Minutes, or null when empty/invalid. */
  value: number | null;
  onChange: (minutes: number | null, raw: string) => void;
  hoursFormat: HoursFormat;
  /** Called with the parsed value when the user presses Enter or leaves the field. */
  onCommit?: (minutes: number | null) => void;
}

/**
 * Text input that accepts `2h 30m`, `2:30`, `2.5` or `150` and reports minutes.
 * The display is normalised to the organization's hours format on blur.
 */
export const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
  ({ value, onChange, hoursFormat, onCommit, onBlur, onKeyDown, ...props }, ref) => {
    const [raw, setRaw] = useState(value === null ? '' : formatDuration(value, hoursFormat));
    const [focused, setFocused] = useState(false);
    // Enter commits and then blurs; remember what was committed so blur does not commit twice.
    const committedRef = useRef<string | null>(null);

    // Keep the display in sync with external changes while not editing.
    useEffect(() => {
      if (!focused) setRaw(value === null ? '' : formatDuration(value, hoursFormat));
    }, [value, hoursFormat, focused]);

    const parse = (text: string) => (text.trim() === '' ? null : parseDuration(text));

    return (
      <Input
        ref={ref}
        inputMode="decimal"
        autoComplete="off"
        value={raw}
        onFocus={() => {
          setFocused(true);
          committedRef.current = null;
        }}
        onChange={(e) => {
          setRaw(e.target.value);
          onChange(parse(e.target.value), e.target.value);
        }}
        onBlur={(e) => {
          setFocused(false);
          const minutes = parse(raw);
          if (minutes !== null) setRaw(formatDuration(minutes, hoursFormat));
          if (committedRef.current !== raw) onCommit?.(minutes);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            committedRef.current = raw;
            onCommit?.(parse(raw));
            e.currentTarget.blur();
          }
          onKeyDown?.(e);
        }}
        {...props}
      />
    );
  },
);
DurationInput.displayName = 'DurationInput';
